
import type { CandidateStatusRaw, StatusHistoryItem } from "../types";

export const CANDIDATE_STATUS_OPTIONS = [
  "All",
  "shortlisted",
  "Under Review",
  "Selected",
  "Rejected",
  "Blacklisted",
] as const;

export type CandidateStatusOption =
  (typeof CANDIDATE_STATUS_OPTIONS)[number];

export const DEPARTMENT_CHART_COLORS = [
  "bg-blue-500",
  "bg-green-500",
  "bg-yellow-500",
  "bg-purple-500",
  "bg-red-500",
] as const;

const normalizeStatusValue = (value: unknown): string => {
  if (typeof value === "string") {
    return value.trim();
  }

  if (typeof value === "number") {
    return String(value);
  }

  if (Array.isArray(value)) {
    for (let index = value.length - 1; index >= 0; index -= 1) {
      const candidate = normalizeStatusValue(value[index]);

      if (candidate) {
        return candidate;
      }
    }

    return "";
  }

  if (value && typeof value === "object") {
    const candidate = value as Record<string, unknown>;

    const directStatus =
      typeof candidate.status === "string"
        ? candidate.status
        : typeof candidate.currentStatus === "string"
          ? candidate.currentStatus
          : "";

    if (directStatus.trim()) {
      return directStatus.trim();
    }
  }

  return "";
};

export const normalizeCandidateStatus = (
  status: CandidateStatusRaw | null | undefined
): string => {
  if (!status) {
    return "Applied";
  }

  const normalized = normalizeStatusValue(status);

  if (!normalized) {
    return "Applied";
  }

  return normalized;
};

export const getStatusText = (
  status: CandidateStatusRaw | null | undefined
): string => {
  const rawStatus = normalizeCandidateStatus(status).trim();

  if (!rawStatus) {
    return "Applied";
  }

  const normalized = rawStatus.toLowerCase();

  const statusMap: Record<string, string> = {
    pendingats: "Pending ATS",
    pending: "Pending ATS",

    underreview: "Under Review",
    "under review": "Under Review",

    selected: "Selected",

    hrinterview: "HR Interview",
    "hr interview": "HR Interview",
    "hr interview scheduled": "HR Interview",

    businessround: "Business Round",
    "business round": "Business Round",

    rejected: "Rejected",

    blacklist: "Blacklisted",
    blacklisted: "Blacklisted",

    hired: "Hired",

    applied: "Applied",
  };

  const exactMatch = Object.keys(statusMap).find(
    (key) =>
      normalized === key ||
      normalized.replace(/\s+/g, "") === key.replace(/\s+/g, "")
  );

  if (exactMatch) {
    return statusMap[exactMatch];
  }

  const prettyStatus = rawStatus
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  return (
    prettyStatus.charAt(0).toUpperCase() +
    prettyStatus.slice(1)
  );
};

export const buildStatusHistory = (
  application:
    | {
        statusHistory?: StatusHistoryItem[] | null;
        status?: CandidateStatusRaw;
        createdAt?: string | null;
      }
    | null,
  formatDate: (value: string | null | undefined) => string
): Array<{ status: string; date: string; time: string }> => {
  if (!application) {
    return [];
  }

  const history = Array.isArray(application.statusHistory)
    ? application.statusHistory
    : [];

  if (history.length > 0) {
    return history
      .filter(
        (item): item is StatusHistoryItem => Boolean(item)
      )
      .map((item) => ({
        status: getStatusText(
          item?.status ?? application.status
        ),
        date: formatDate(
          item?.date ?? application.createdAt
        ),
        time: item?.time ?? "",
      }));
  }

  return [
    {
      status: getStatusText(application.status),
      date: formatDate(application.createdAt),
      time: "",
    },
  ];
};
